import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Lock } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}

export function EmptyState({ icon, title, description, action, className, compact = false }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'px-4 py-8' : 'px-6 py-16', className)}>
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
        {icon ?? <Inbox size={20} aria-hidden="true" />}
      </span>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-[13px] text-ink-secondary">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function LoadingState({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={cn('flex items-center justify-center gap-2.5 px-6 py-16 text-[13px] text-ink-secondary', className)}>
      <Spinner className="text-primary" />
      {label}
    </div>
  );
}

export function ErrorState({ title = 'Something went wrong', message, action }: { title?: string; message: string; action?: ReactNode }) {
  return (
    <EmptyState
      icon={<AlertCircle size={20} aria-hidden="true" className="text-danger" />}
      title={title}
      description={message}
      action={action}
    />
  );
}

export function NoAccessState({ message = 'Your role does not include access to this section. Contact a Super Admin if you need it.' }: { message?: string }) {
  return <EmptyState icon={<Lock size={20} aria-hidden="true" />} title="You don't have access" description={message} />;
}

export function SkeletonRows({ rows = 6, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c} className="h-14 px-4">
              <div className={cn('h-3 animate-pulse rounded bg-surface-sunken', c === 0 ? 'w-40' : 'w-16')} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
